import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/fuso-logo.svg';
import translations from '../translations';
import { useToast, Spinner } from '@chakra-ui/react';
import LanguageDropDown from '../languageDropDown/dropDown';
import ProfilePhoto from '../profilePhoto/profilePhoto';
import { useAdmin } from '../context/AdminContext';

const RECORDS_PER_PAGE = 20;

// Environment-based API URL configuration
const SERVER_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000/' : '/api/';

// Attachment URLs come from user-submitted posts. Only http(s) may reach an
// href/src — a stored `javascript:` URL would otherwise run on the admin's click.
const safeUrl = (url) => {
    try {
        const parsed = new URL(url);
        return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
    } catch (e) {
        return null;
    }
};

// ImageBlobUrls is a JSON array string; returns only the safe URLs in it.
const parseImageUrls = (raw) => {
    try {
        const urls = JSON.parse(raw);
        return Array.isArray(urls) ? urls.map(safeUrl).filter(Boolean) : [];
    } catch (e) {
        return [];
    }
};

// When a distributor post was made, for display.
const formatPostDate = (post) => {
    const postedAt = post.Timestamp || post.CreatedAt;
    return postedAt ? new Date(postedAt).toLocaleString() : '—';
};

// Markdown/index pipeline state of a processed post: [badge class, label].
const processingState = (post) => {
    if (post.MarkdownStatus.startsWith('failed')) return ['rejected', 'Markdown failed'];
    if (post.IngestionStatus === 'ingested') return ['approved', 'Indexed'];
    if (post.IngestionStatus === 'failed') return ['rejected', 'Index failed'];
    return ['pending', 'Preparing'];
};

// Access-request filters: the stat card and filter button for each status.
const REQUEST_FILTERS = [
    { key: 'all', stat: 'total', cardLabel: 'Total Requests', buttonLabel: 'All' },
    { key: 'pending', stat: 'pending', cardLabel: 'Pending', buttonLabel: 'Pending' },
    { key: 'approved', stat: 'approved', cardLabel: 'Approved', buttonLabel: 'Approved' },
    { key: 'rejected', stat: 'rejected', cardLabel: 'Rejected', buttonLabel: 'Rejected' },
];

const CREATE_USER_FIELDS = [
    { name: 'user_id', label: 'User ID', type: 'text', placeholder: 'Enter user ID', required: true },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'user@example.com', required: true },
    { name: 'name', label: 'Name', type: 'text', placeholder: 'Enter full name', required: true },
    { name: 'company_name', label: 'Company', type: 'text', placeholder: 'Enter company name' },
    { name: 'branch', label: 'Branch', type: 'text', placeholder: 'Enter branch' },
    { name: 'department', label: 'Department', type: 'text', placeholder: 'Enter department' },
];

const POST_TEXT_FIELDS = [
    { label: 'Issue summary', field: 'IssueSummary' },
    { label: 'Repair details', field: 'RepairDetails' },
    { label: 'Reason', field: 'Reason' },
];

function LoadingSpinner() {
    return (
        <div className="loading-container">
            <Spinner size="xl" color="#E31937" thickness="4px" label="" />
        </div>
    );
}

function EmptyRow({ colSpan, text }) {
    return <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: '40px' }}>{text}</td></tr>;
}

function Pagination({ page, totalPages, totalItems, onGo, translate }) {
    if (totalPages <= 1) return null;
    return (
        <div className="pagination">
            <span className="pagination-info">
                {translate('Showing')} {(page - 1) * RECORDS_PER_PAGE + 1} - {Math.min(page * RECORDS_PER_PAGE, totalItems)} {translate('of')} {totalItems}
            </span>
            <div className="pagination-controls">
                <button onClick={() => onGo(1)} disabled={page === 1}>«</button>
                <button onClick={() => onGo(page - 1)} disabled={page === 1}>‹</button>
                <span className="page-number">{translate('Page')} {page} / {totalPages}</span>
                <button onClick={() => onGo(page + 1)} disabled={page === totalPages}>›</button>
                <button onClick={() => onGo(totalPages)} disabled={page === totalPages}>»</button>
            </div>
        </div>
    );
}

function SidebarNav({ isOpen, activeTab, onSelect, translate }) {
    return (
        <aside className={`dashboard-sidebar ${isOpen ? 'open' : 'closed'}`}>
            <nav className="sidebar-nav">
                <button
                    className={`nav-item ${activeTab === 'requests' ? 'active' : ''}`}
                    onClick={() => onSelect('requests')}
                >
                    <span className="nav-icon">
                        <img src="./approval.png" alt="Request Approval" />
                    </span>
                    {isOpen && <span className="nav-label">{translate('Request Approval')}</span>}
                </button>
                <button
                    className={`nav-item ${activeTab === 'distributor-posts' ? 'active' : ''}`}
                    onClick={() => onSelect('distributor-posts')}
                >
                    <span className="nav-icon">
                        <img src="./add.png" alt="Distributor posts" onError={(e) => { e.target.style.display = 'none'; }} />
                    </span>
                    {isOpen && <span className="nav-label">{translate('Distributor posts')}</span>}
                </button>
                {/* Users tab - hidden for now
                <button
                    className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => onSelect('users')}
                >
                    <span className="nav-icon">
                        <img src="./users-grey.png" alt="Users" />
                    </span>
                    {isOpen && <span className="nav-label">{translate('Users')}</span>}
                </button>
                */}
            </nav>
        </aside>
    );
}

// Long free text is cut to 40 characters in the table; the tooltip has it all.
function TruncatedCell({ text }) {
    return <td title={text}>{(text || '—').slice(0, 40)}{(text && text.length > 40) ? '…' : ''}</td>;
}

function PostAttachmentLink({ post, translate }) {
    const pdfUrl = safeUrl(post.PdfBlobUrl);
    if (pdfUrl) {
        return (
            <a href={pdfUrl} target="_blank" rel="noreferrer">
                {post.PdfFileName || translate('Open')}
            </a>
        );
    }
    const urls = post.ImageBlobUrls ? parseImageUrls(post.ImageBlobUrls) : [];
    if (!urls.length) return '—';
    return (
        <a href={urls[0]} target="_blank" rel="noreferrer">
            {urls.length > 1 ? `${translate('Images')} (${urls.length})` : translate('Open')}
        </a>
    );
}

function ProcessingBadge({ post, translate }) {
    if (!(post.PdfBlobUrl || post.ImageBlobUrls)) return '—';
    if (!post.MarkdownStatus) {
        return post.Status === 'approved' ? <span className="status-badge pending">{translate('Preparing')}</span> : '—';
    }
    const [badge, label] = processingState(post);
    return (
        <span className={`status-badge ${badge}`} title={post.ProcessingError || ''}>
            {translate(label)}
        </span>
    );
}

function DistributorPostRow({ post, isApproving, onView, onApprove, translate }) {
    return (
        <tr>
            <td>{formatPostDate(post)}</td>
            <td>{post.UserEmail || '—'}</td>
            <td>{post.VehicleModel || '—'}</td>
            <TruncatedCell text={post.IssueSummary} />
            <TruncatedCell text={post.RepairDetails} />
            <TruncatedCell text={post.Reason} />
            <td><PostAttachmentLink post={post} translate={translate} /></td>
            <td><span className={`status-badge ${(post.Status || '').toLowerCase()}`}>{post.Status || '—'}</span></td>
            <td><ProcessingBadge post={post} translate={translate} /></td>
            <td>
                <button className="action-btn view-btn" onClick={() => onView(post)}>{translate('View')}</button>
                {post.Status === 'pending' && (
                    <button
                        className="action-btn approve-btn"
                        onClick={() => onApprove(post.PartitionKey, post.RowKey)}
                        disabled={isApproving}
                    >
                        {isApproving ? (
                            <span className="approve-btn-loading"><Spinner size="sm" label="" /> {translate('Approving')}...</span>
                        ) : (
                            translate('Approve')
                        )}
                    </button>
                )}
            </td>
        </tr>
    );
}

function DistributorPostsTab({ posts, loading, approvingPostKey, onView, onApprove, translate }) {
    return (
        <>
            <div className="dashboard-title-section">
                <h1 className="dashboard-title">{translate('Distributor posts')}</h1>
            </div>
            <div className="table-section">
                <div className="table-header">
                    <h2>{translate('Distributor posts')}</h2>
                </div>
                {loading ? (
                    <div className="loading-container loading-container-distributor">
                        <Spinner size="xl" color="#E31937" thickness="4px" label="" emptyColor="gray.200" />
                        <span className="loading-label">{translate('Loading')}</span>
                    </div>
                ) : (
                    <div className="requests-table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>{translate('Date')}</th>
                                    <th>{translate('Email')}</th>
                                    <th>{translate('Vehicle model')}</th>
                                    <th>{translate('Issue summary')}</th>
                                    <th>{translate('Repair details')}</th>
                                    <th>{translate('Reason')}</th>
                                    <th>{translate('PDF')}</th>
                                    <th>{translate('Status')}</th>
                                    <th>{translate('Processing')}</th>
                                    <th>{translate('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {posts.length === 0 ? (
                                    <EmptyRow colSpan="10" text={translate('No requests found')} />
                                ) : (
                                    posts.map((post) => {
                                        const postKey = `${post.PartitionKey}-${post.RowKey}`;
                                        return (
                                            <DistributorPostRow
                                                key={postKey}
                                                post={post}
                                                isApproving={approvingPostKey === postKey}
                                                onView={onView}
                                                onApprove={onApprove}
                                                translate={translate}
                                            />
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

function RequestCharts({ stats, pieData, translate }) {
    return (
        <div className="charts-row">
            <div className="card chart-card">
                <h3>{translate('Requests Over Time')}</h3>
                <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                        <AreaChart data={stats.daily || []} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#E31937" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#E31937" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e8e9" />
                            <XAxis dataKey="name" stroke="#6c757d" fontSize={12} />
                            <YAxis stroke="#6c757d" fontSize={12} />
                            <Tooltip
                                contentStyle={{ background: '#fff', border: '1px solid #e5e8e9', borderRadius: '8px' }}
                                labelStyle={{ fontWeight: '600' }}
                            />
                            <Area type="monotone" dataKey="requests" stroke="#E31937" strokeWidth={2} fill="url(#colorRequests)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="card chart-card">
                <h3>{translate('Request Distribution')}</h3>
                <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="45%"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ background: '#fff', border: '1px solid #e5e8e9', borderRadius: '8px' }}
                                formatter={(value, name) => [`${value}`, name]}
                            />
                            <Legend
                                verticalAlign="bottom"
                                height={36}
                                formatter={(value, entry) => (
                                    <span style={{ color: '#282f32', fontSize: '13px' }}>{value} ({entry.payload.value})</span>
                                )}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

function RequestRow({ req, onAction, translate }) {
    return (
        <tr>
            <td>{new Date(req.RequestedAt).toLocaleDateString()}</td>
            <td>{req.Name}</td>
            <td>{req.Email}</td>
            <td>{req.CompanyName}</td>
            <td>{req.Branch}</td>
            <td><span className={`status-badge ${req.Status.toLowerCase()}`}>{translate(req.Status)}</span></td>
            <td>
                {req.Status === 'Pending' ? (
                    <>
                        <button className="action-btn approve-btn" onClick={() => onAction(req.PartitionKey, req.RowKey, 'approve')}>{translate('Approve')}</button>
                        <button className="action-btn reject-btn" onClick={() => onAction(req.PartitionKey, req.RowKey, 'reject')}>{translate('Reject')}</button>
                    </>
                ) : <span className="no-action">—</span>}
            </td>
        </tr>
    );
}

function RequestsTab({ stats, pieData, activeFilter, onFilter, onExport, loading, requests, onAction, pagination, translate }) {
    return (
        <>
            <div className="dashboard-title-section">
                <h1 className="dashboard-title">{translate('Admin Dashboard')}</h1>
            </div>
            <div className="stat-cards">
                {REQUEST_FILTERS.map((f) => (
                    <div key={f.key} className={`stat-card ${f.stat} ${activeFilter === f.key ? 'selected' : ''}`} onClick={() => onFilter(f.key)}>
                        <span className="stat-value">{stats[f.stat]}</span>
                        <span className="stat-label">{translate(f.cardLabel)}</span>
                    </div>
                ))}
            </div>

            <RequestCharts stats={stats} pieData={pieData} translate={translate} />

            <div className="table-section">
                <div className="table-header">
                    <h2>{translate('Access Requests')}</h2>
                    <div className="table-controls">
                        <div className="filter-buttons">
                            {REQUEST_FILTERS.map((f) => (
                                <button key={f.key} className={`filter-btn ${activeFilter === f.key ? 'active' : ''}`} onClick={() => onFilter(f.key)}>{translate(f.buttonLabel)} ({stats[f.stat]})</button>
                            ))}
                        </div>
                        <button className="export-btn" onClick={onExport}>📥 {translate('Export CSV')}</button>
                    </div>
                </div>

                {loading ? <LoadingSpinner /> : (
                    <>
                        <div className="requests-table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>{translate('Date')}</th>
                                        <th>{translate('Name')}</th>
                                        <th>{translate('Email')}</th>
                                        <th>{translate('Company')}</th>
                                        <th>{translate('Branch')}</th>
                                        <th>{translate('Status')}</th>
                                        <th>{translate('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length === 0 ? (
                                        <EmptyRow colSpan="7" text={translate('No requests found')} />
                                    ) : (
                                        requests.map((req) => (
                                            <RequestRow key={req.RowKey} req={req} onAction={onAction} translate={translate} />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination {...pagination} translate={translate} />
                    </>
                )}
            </div>
        </>
    );
}

function UserRow({ user, translate }) {
    return (
        <tr>
            <td>{user.gemsId || user.uid || '—'}</td>
            <td>{user.firstName || '—'}</td>
            <td>{user.lastName || '—'}</td>
            <td>{user.email || user.businessIdEmail || '—'}</td>
            <td>{user.companyName || '—'}</td>
            <td>{user.branch || '—'}</td>
            <td>{user.department || '—'}</td>
            <td>
                <span className={`status-badge ${user.isAdmin ? 'approved' : 'rejected'}`}>
                    {user.isAdmin ? translate('Yes') : translate('No')}
                </span>
            </td>
        </tr>
    );
}

function UsersTab({ users, loading, pagination, translate }) {
    return (
        <>
            <div className="dashboard-title-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 className="dashboard-title">{translate('Users')}</h1>
                {/* TODO: Enable manual user creation in future
                <button className="export-btn" onClick={() => setIsCreateUserModalOpen(true)}>
                    ➕ {translate('Create User')}
                </button>
                */}
            </div>

            <div className="table-section">

                {loading ? <LoadingSpinner /> : (
                    <>
                        <div className="requests-table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>{translate('User ID')}</th>
                                        <th>{translate('First Name')}</th>
                                        <th>{translate('Last Name')}</th>
                                        <th>{translate('Email')}</th>
                                        <th>{translate('Company')}</th>
                                        <th>{translate('Branch')}</th>
                                        <th>{translate('Department')}</th>
                                        <th>{translate('Admin')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 ? (
                                        <EmptyRow colSpan="8" text={translate('No users found')} />
                                    ) : (
                                        users.map((user, index) => (
                                            <UserRow key={user.uid || index} user={user} translate={translate} />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination {...pagination} translate={translate} />
                    </>
                )}
            </div>
        </>
    );
}

function ReadOnlyEmpty() {
    return <input className="add-info-input read-only" readOnly value="—" />;
}

function PostImages({ raw }) {
    const urls = raw ? parseImageUrls(raw) : [];
    if (!urls.length) return <ReadOnlyEmpty />;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {urls.map((url, idx) => (
                <a key={idx} href={url} target="_blank" rel="noreferrer">
                    <img
                        src={url}
                        alt={`attachment-${idx + 1}`}
                        style={{ maxWidth: '140px', maxHeight: '140px', borderRadius: '8px' }}
                    />
                </a>
            ))}
        </div>
    );
}

function ViewPostModal({ post, onClose, translate }) {
    const pdfUrl = safeUrl(post.PdfBlobUrl);
    const meta = [
        { label: 'Date', value: formatPostDate(post) },
        { label: 'Email', value: post.UserEmail || '—' },
        { label: 'Vehicle model', value: post.VehicleModel || '—' },
    ];
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-content-view-post" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{translate('Case study of repair and troubleshooting')}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={translate('Close')}>×</button>
                </div>
                <div className="modal-body modal-body-view-post">
                    <div className="view-post-meta">
                        {meta.map((m) => (
                            <div key={m.label} className="view-post-meta-item">
                                <span className="view-post-meta-label">{translate(m.label)}</span>
                                <span className="view-post-meta-value">{m.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="view-post-divider" />
                    <div className="view-post-fields">
                        {POST_TEXT_FIELDS.map((f) => (
                            <div key={f.field} className="add-info-field">
                                <label className="add-info-label">{translate(f.label)}</label>
                                <textarea className="add-info-textarea read-only" readOnly rows={5} value={post[f.field] || '—'} />
                            </div>
                        ))}
                        <div className="add-info-field">
                            <label className="add-info-label">{translate('PDF')}</label>
                            {pdfUrl ? (
                                <div>
                                    <a href={pdfUrl} target="_blank" rel="noreferrer">
                                        {post.PdfFileName || translate('Open')}
                                    </a>
                                </div>
                            ) : (
                                <ReadOnlyEmpty />
                            )}
                        </div>
                        <div className="add-info-field">
                            <label className="add-info-label">{translate('Images')}</label>
                            <PostImages raw={post.ImageBlobUrls} />
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        {translate('Close')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CreateUserModal({ form, setForm, loading, onCreate, onClose, translate }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{translate('Create User')}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {CREATE_USER_FIELDS.map((f) => (
                        <div key={f.name} className="form-group">
                            <label>{translate(f.label)}{f.required && ' *'}</label>
                            <input
                                type={f.type}
                                value={form[f.name]}
                                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                placeholder={f.placeholder}
                            />
                        </div>
                    ))}
                </div>
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        {translate('Cancel')}
                    </button>
                    <button
                        className="create-btn"
                        onClick={onCreate}
                        disabled={loading || !form.user_id || !form.email || !form.name}
                    >
                        {loading ? translate('Creating user') : translate('Create')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdminDashboard() {
    const navigate = useNavigate();
    const toast = useToast({ position: 'top-right' });
    const [activeFilter, setActiveFilter] = useState('all');
    const [allRequests, setAllRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, daily: [] });
    const [currentPage, setCurrentPage] = useState(1);
    const [langOpt, setLangOpt] = useState(() => sessionStorage.getItem('langOpt') || 'Japanese');

    // Sidebar and tab state
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('requests'); // 'requests' | 'users' | 'distributor-posts'

    // Distributor / case study posts state
    const [distributorPosts, setDistributorPosts] = useState([]);
    const [distributorPostsLoading, setDistributorPostsLoading] = useState(false);
    const [selectedDistributorPost, setSelectedDistributorPost] = useState(null);
    const [isViewPostModalOpen, setIsViewPostModalOpen] = useState(false);
    const [approvingPostKey, setApprovingPostKey] = useState(null); // PartitionKey-RowKey of post being approved

    // Users data state
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotal, setUsersTotal] = useState(0);

    // Create user modal state
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [createUserLoading, setCreateUserLoading] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({
        user_id: '',
        email: '',
        name: '',
        company_name: '',
        branch: '',
        department: ''
    });

    // Get admin status from context (already checked once globally)
    const { isAdmin, loading: adminCheckLoading } = useAdmin();

    useEffect(() => {
        // Only redirect after admin check completes
        if (!adminCheckLoading && !isAdmin) {
            toast({
                title: 'Access Denied',
                description: 'You do not have admin privileges',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            navigate('/');
        }
    }, [isAdmin, adminCheckLoading, navigate, toast]);

    useEffect(() => {
        sessionStorage.setItem('langOpt', langOpt);
    }, [langOpt]);

    const CHART_COLORS = {
        pending: '#E31937',
        approved: '#2abdbb',
        rejected: '#6c757d',
        total: '#282f32'
    };

    const translate = (text) => {
        if (!text || !translations[text]) return text;
        switch (langOpt) {
            case 'Japanese': return translations[text].translationJp || text;
            case 'Indonesian': return translations[text].translationIn || text;
            case 'Nepali': return translations[text].translationNp || text;
            case 'Sinhala': return translations[text].translationSl || text;
            case 'Vietnamese': return translations[text].translationVn || text;
            case 'Filipino': return translations[text].translationPh || text;
            case 'Bengali': return translations[text].translationBd || text;
            case 'English':
            default: return translations[text].translationEng || text;
        }
    };

    useEffect(() => {
        fetchData();
        fetchStats();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter]);

    useEffect(() => {
        if (activeTab === 'users') {
            fetchUsers(1);
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'distributor-posts') {
            fetchDistributorPosts();
        }
    }, [activeTab]);

    const fetchDistributorPosts = async () => {
        setDistributorPostsLoading(true);
        try {
            const res = await fetch(`${SERVER_URL}admin/distributor-posts`);
            const data = await res.json();
            if (data.status && Array.isArray(data.data)) {
                const sorted = [...data.data].sort((a, b) => {
                    const aPending = (a.Status || '').toLowerCase() === 'pending';
                    const bPending = (b.Status || '').toLowerCase() === 'pending';
                    if (aPending && !bPending) return -1;
                    if (!aPending && bPending) return 1;
                    return new Date(b.Timestamp || b.CreatedAt || 0) - new Date(a.Timestamp || a.CreatedAt || 0);
                });
                setDistributorPosts(sorted);
            } else {
                setDistributorPosts([]);
                if (!res.ok || (data.status === false && data.error)) {
                    toast({
                        title: translate('Error'),
                        description: data.error || translate('Failed to load distributor posts'),
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching distributor posts:', error);
            setDistributorPosts([]);
            toast({
                title: translate('Error'),
                description: translate('Failed to load distributor posts'),
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setDistributorPostsLoading(false);
        }
    };

    const handleApproveDistributorPost = async (partitionKey, rowKey) => {
        const key = `${partitionKey}-${rowKey}`;
        setApprovingPostKey(key);
        try {
            const response = await fetch(`${SERVER_URL}admin/distributor-posts/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ partition_key: partitionKey, row_key: rowKey })
            });
            const result = await response.json();
            if (result.status) {
                toast({
                    title: translate('Success'),
                    description: result.message || translate('Post approved'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                await fetchDistributorPosts();
            } else {
                toast({
                    title: translate('Error'),
                    description: result.error || translate('Failed to approve'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error approving post:', error);
            toast({
                title: translate('Error'),
                description: translate('Error approving post'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setApprovingPostKey(null);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await fetch(`${SERVER_URL}admin/stats`);
            const data = await res.json();
            if (data.status) {
                setStats(data.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const pendingRes = await fetch(`${SERVER_URL}admin/requests/pending?page=1&size=500`);
            const pendingData = await pendingRes.json();
            const approvedRes = await fetch(`${SERVER_URL}admin/requests/approved?page=1&size=500`);
            const approvedData = await approvedRes.json();

            const pending = pendingData.status ? pendingData.data : [];
            const approved = approvedData.status ? approvedData.data : [];

            const combined = [...pending, ...approved].sort((a, b) =>
                new Date(b.RequestedAt) - new Date(a.RequestedAt)
            );
            setAllRequests(combined);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async (page = 1) => {
        setUsersLoading(true);
        try {
            const res = await fetch(`${SERVER_URL}admin/users?page=${page}&size=${RECORDS_PER_PAGE}`);
            const data = await res.json();
            if (data.status) {
                setUsers(data.data);
                setUsersTotal(data.total);
                setUsersPage(page);
            } else {
                toast({
                    title: 'Error',
                    description: 'Failed to fetch users',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            toast({
                title: 'Error',
                description: 'Error fetching users',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUsersLoading(false);
        }
    };

    const handleAction = async (userId, rowKey, action) => {
        try {
            const url = action === 'approve' ? `${SERVER_URL}admin/requests/approve` : `${SERVER_URL}admin/requests/reject`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId, row_key: rowKey, approver_id: 'admin' })
            });
            const result = await response.json();
            if (result.status) {
                toast({
                    title: 'Success',
                    description: `Request ${action}d successfully`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                fetchData();
                fetchStats();
            } else {
                toast({
                    title: 'Error',
                    description: `Failed to ${action}: ${result.error}`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error(`Error ${action}ing request:`, error);
            toast({
                title: 'Error',
                description: `Error ${action}ing request`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const exportToCSV = () => {
        const filteredData = getFilteredRequests();
        if (filteredData.length === 0) {
            toast({
                title: 'Warning',
                description: 'No data to export',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const headers = ['Date', 'Name', 'Email', 'Company', 'Branch', 'Status'];
        const csvContent = [
            headers.join(','),
            ...filteredData.map(req => [
                new Date(req.RequestedAt).toLocaleDateString(),
                req.Name,
                req.Email,
                req.CompanyName,
                req.Branch,
                req.Status
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `requests_${activeFilter}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const getFilteredRequests = () => {
        if (activeFilter === 'all') return allRequests;
        return allRequests.filter(req => req.Status.toLowerCase() === activeFilter);
    };

    const pieData = [
        { name: translate('Pending'), value: stats.pending, color: CHART_COLORS.pending },
        { name: translate('Approved'), value: stats.approved, color: CHART_COLORS.approved },
        { name: translate('Rejected'), value: stats.rejected, color: CHART_COLORS.rejected },
    ].filter(d => d.value > 0);

    const filteredRequests = getFilteredRequests();
    const totalPages = Math.ceil(filteredRequests.length / RECORDS_PER_PAGE);
    const paginatedRequests = filteredRequests.slice(
        (currentPage - 1) * RECORDS_PER_PAGE,
        currentPage * RECORDS_PER_PAGE
    );

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Pagination for users tab
    const usersTotalPages = Math.ceil(usersTotal / RECORDS_PER_PAGE);
    const goToUsersPage = (page) => {
        if (page >= 1 && page <= usersTotalPages) {
            fetchUsers(page);
        }
    };

    const handleCreateUser = async () => {
        setCreateUserLoading(true);
        try {
            const response = await fetch(`${SERVER_URL}admin/users/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(createUserForm)
            });
            const result = await response.json();

            if (result.status) {
                toast({
                    title: translate('User created successfully'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                setIsCreateUserModalOpen(false);
                setCreateUserForm({
                    user_id: '',
                    email: '',
                    name: '',
                    company_name: '',
                    branch: '',
                    department: ''
                });
                fetchUsers(usersPage); // Refresh users list
            } else {
                toast({
                    title: translate('Failed to create user'),
                    description: result.message || result.error,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error creating user:', error);
            toast({
                title: translate('Failed to create user'),
                description: 'Network error',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setCreateUserLoading(false);
        }
    };

    const openViewPost = (post) => {
        setSelectedDistributorPost(post);
        setIsViewPostModalOpen(true);
    };
    const closeViewPost = () => {
        setIsViewPostModalOpen(false);
        setSelectedDistributorPost(null);
    };

    let tabContent;
    if (activeTab === 'distributor-posts') {
        tabContent = (
            <DistributorPostsTab
                posts={distributorPosts}
                loading={distributorPostsLoading}
                approvingPostKey={approvingPostKey}
                onView={openViewPost}
                onApprove={handleApproveDistributorPost}
                translate={translate}
            />
        );
    } else if (activeTab === 'requests') {
        tabContent = (
            <RequestsTab
                stats={stats}
                pieData={pieData}
                activeFilter={activeFilter}
                onFilter={setActiveFilter}
                onExport={exportToCSV}
                loading={loading}
                requests={paginatedRequests}
                onAction={handleAction}
                pagination={{ page: currentPage, totalPages, totalItems: filteredRequests.length, onGo: goToPage }}
                translate={translate}
            />
        );
    } else {
        tabContent = (
            <UsersTab
                users={users}
                loading={usersLoading}
                pagination={{ page: usersPage, totalPages: usersTotalPages, totalItems: usersTotal, onGo: goToUsersPage }}
                translate={translate}
            />
        );
    }

    return (
        <div className="admin-dashboard">
            <header className="fuso-header">
                <div className="header-left">
                    <button
                        className="sidebar-toggle"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        aria-label="Toggle sidebar"
                    >
                        <div className="hamburger-icon">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </button>
                    <img src={logo} alt="Fuso Logo" className="fuso-logo" />
                </div>
                <div className="header-toolbar">
                    <button className="back-btn" onClick={() => navigate('/tic-bot')}>{translate('Back to Chat')}</button>
                    <LanguageDropDown setLanguage={setLangOpt} translate={translate} showAllLanguages={false} />
                    <ProfilePhoto translate={translate} />
                </div>
            </header>

            <div className="dashboard-container">
                <SidebarNav isOpen={isSidebarOpen} activeTab={activeTab} onSelect={setActiveTab} translate={translate} />

                {/* Main Content */}
                <main className={`dashboard-main ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                    {tabContent}
                </main>
            </div>

            {/* View Distributor Post Modal (read-only) */}
            {isViewPostModalOpen && selectedDistributorPost && (
                <ViewPostModal post={selectedDistributorPost} onClose={closeViewPost} translate={translate} />
            )}

            {/* Create User Modal */}
            {isCreateUserModalOpen && (
                <CreateUserModal
                    form={createUserForm}
                    setForm={setCreateUserForm}
                    loading={createUserLoading}
                    onCreate={handleCreateUser}
                    onClose={() => setIsCreateUserModalOpen(false)}
                    translate={translate}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
